/* eslint-disable no-nested-ternary */
import { knownCurrencySymbols } from '../../constants/knownCurrencySymbols';
import {
  areas,
  formatCurrency,
  formatShares,
  investmentInstruments,
  listingTypes,
  sectors,
  offerTypes,
} from 'entoro-shared';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';

const formatAllCurrency = (value, currency = 'USD') => {
  // Validate currency code
  const currencyCodes = new Set(Intl.supportedValuesOf('currency'));
  const validCurrencyCode = currencyCodes.has(currency) ? currency : 'USD';

  // Check if we have a known symbol for this currency
  if (knownCurrencySymbols[validCurrencyCode]) {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: 'USD', // Temporarily use USD, we'll replace the symbol
    })
      .format(value)
      .replace('$', knownCurrencySymbols[validCurrencyCode]);
  }

  // If we don't have a known symbol, proceed with regular formatting
  const formattedValue = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: validCurrencyCode,
    currencyDisplay: 'symbol',
    currencySign: 'accounting',
  }).format(value);

  return formattedValue;
};

const OfferDetails = ({
  offer: {
    name,
    listingType,
    investmentInstrument,
    area,
    minimumInvestment,
    targetRaise,
    offerCurrency,
    valueRange,
    assetStatus,
    sharesForSale,
    sector,
    amountRaise,
    totalInvestors,
    issuerCompany,
    primaryObjective,
    netAcres,
    county,
    averageDailyProduction,
    typeOfOffer,
    headerType,
    valuationCap,
    maturity,
    coupon,
    discountRate,
    closingDate,
    countDown,
    productDeveloper,
    creditIssued,
    product,
    vintage,
    region,
  },
  disabledRepublic,
  CompanyName,
  OfferName,
}) => {
  const date = moment(countDown).format('YYYY-MM-DD');
  const Closingday = closingDate
    ? moment(closingDate).format('YYYY-MM-DD')
    : '';
  const difference = +new Date(`${date}`) - +new Date();
  const openInvestmentHour = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const openInvestmentDay = Math.floor(difference / (1000 * 60 * 60 * 24));

  return (
    <div>
      <div className='w-row'>
        <div className='column-2 w-col w-col-8'>
          <div className='header-container'>
            {headerType === 'VIDEO' && (
              <Fragment>
                <div className='_16-black-label'>Company&nbsp;Name</div>
                <div
                  className='black-44'
                  style={{ fontWeight: 'normal' }}
                >{`${CompanyName}`}</div>
              </Fragment>
            )}
            {headerType === 'IMAGE' ? (
              <div className='black-44'>{OfferName} Details</div>
            ) : (
              <div className='black-44' style={{ fontSize: '35px' }}>
                {OfferName} Details
              </div>
            )}

            {typeOfOffer && !difference && (
              <Fragment>
                <div className='_16-black-label'>Type&nbsp;Of&nbsp;Offer</div>
                <div className='_26-black'>
                  {offerTypes.getLabel(typeOfOffer)}
                </div>
              </Fragment>
            )}
            {typeOfOffer && difference > 0 && (
              <Fragment>
                <div className='_16-black-label'>Type&nbsp;Of&nbsp;Offer</div>
                <div className='_26-black'>
                  {offerTypes.getLabel(typeOfOffer)}
                </div>
              </Fragment>
            )}
          </div>
          {!disabledRepublic && (
            <Fragment>
              {typeOfOffer === 'REG_CF' && (
                <div className='detail-columns w-row'>
                  <div className='w-col w-col-6'>
                    {typeOfOffer === 'REG_CF' && (
                      <Fragment>
                        {difference > 0 && (
                          <Fragment>
                            {' '}
                            <div className='_16-black-label'>
                              Accepting Investment in
                            </div>
                            <div className='_26-black'>
                              {openInvestmentDay !== 0
                                ? ` ${openInvestmentDay} Days`
                                : ` ${openInvestmentHour} Hours`}
                            </div>
                          </Fragment>
                        )}
                        {typeOfOffer && difference < 0 && (
                          <Fragment>
                            <div className='_16-black-label'>
                              Type&nbsp;Of&nbsp;Offer
                            </div>
                            <div className='_26-black'>
                              {offerTypes.getLabel(typeOfOffer)}
                            </div>
                          </Fragment>
                        )}
                      </Fragment>
                    )}
                  </div>
                  <div className='w-col w-col-6'>
                    {typeOfOffer === 'REG_CF' && Closingday && (
                      <Fragment>
                        <div className='_16-black-label'> Closing Date</div>
                        <div className='_26-black'>{Closingday}</div>
                      </Fragment>
                    )}
                  </div>
                </div>
              )}
              <div className='detail-columns w-row'>
                <div className='w-col w-col-6'>
                  {typeOfOffer === 'REG_CF' && (
                    <Fragment>
                      <div className='_16-black-label'>Amount Raised</div>
                      <div className='_26-black'>
                        {formatAllCurrency(amountRaise, offerCurrency) || 'N/A'}
                      </div>
                    </Fragment>
                  )}
                </div>
                <div className='w-col w-col-6'>
                  {typeOfOffer === 'REG_CF' && (
                    <Fragment>
                      <div className='_16-black-label'>Total Investors</div>
                      <div className='_26-black'>{totalInvestors}</div>
                    </Fragment>
                  )}
                </div>
              </div>
            </Fragment>
          )}
          <div className='detail-columns w-row'>
            <div className='w-col w-col-6'>
              {listingType === 'SECONDARY_SALES' && (
                <Fragment>
                  <div className='_16-black-label'>
                    Shares&nbsp;for&nbsp;Sale
                  </div>
                  <div className='_26-black'>{formatShares(sharesForSale)}</div>
                </Fragment>
              )}
              {listingType === 'ACQUISITIONS_AND_DIVESTITURES' && (
                <Fragment>
                  <div className='_16-black-label'>
                    Average&nbsp;Daily&nbsp;Production
                  </div>
                  <div className='_26-black'>{averageDailyProduction}</div>
                </Fragment>
              )}
              {listingType === 'ASSET_SALES' && (
                <Fragment>
                  <div className='_16-black-label'>Value&nbsp;Range</div>
                  <div className='_26-black'>{valueRange}</div>
                </Fragment>
              )}
              {listingType !== 'SECONDARY_SALES' &&
                listingType !== 'ACQUISITIONS_AND_DIVESTITURES' &&
                listingType !== 'ASSET_SALES' &&
                typeOfOffer !== 'Carbon_Credit' && (
                  <Fragment>
                    <div className='_16-black-label'>
                      Target&nbsp;Investment
                    </div>
                    <div className='_26-black'>
                      {formatAllCurrency(targetRaise, offerCurrency) || 'N/A'}
                    </div>
                  </Fragment>
                )}

              {typeOfOffer === 'Carbon_Credit' && (
                <Fragment>
                  <div className='_16-black-label'>Project&nbsp;Developer</div>
                  <div className='_26-black'>{productDeveloper || 'N/A'}</div>
                </Fragment>
              )}
            </div>
            <div className='w-col w-col-6'>
              {listingType === 'ACQUISITIONS_AND_DIVESTITURES' && (
                <Fragment>
                  <div className='_16-black-label'>Net&nbsp;Acres</div>
                  <div className='_26-black'>
                    {formatShares(netAcres) || 'N/A'}
                  </div>
                </Fragment>
              )}
              {listingType === 'ASSET_SALES' && (
                <Fragment>
                  <div className='_16-black-label'>Asset&nbsp;Status</div>
                  <div className='_26-black'>{assetStatus}</div>
                </Fragment>
              )}
              {listingType !== 'ACQUISITIONS_AND_DIVESTITURES' &&
                listingType !== 'ASSET_SALES' &&
                typeOfOffer !== 'Carbon_Credit' && (
                  <Fragment>
                    <div className='_16-black-label'>
                      Minimum&nbsp;Investment
                    </div>
                    <div className='_26-black'>
                      {formatAllCurrency(minimumInvestment, offerCurrency) ||
                        'N/A'}
                    </div>
                  </Fragment>
                )}
              {typeOfOffer === 'Carbon_Credit' && (
                <Fragment>
                  <div className='_16-black-label'>Product</div>
                  <div className='_26-black'>{product || 'N/A'}</div>
                </Fragment>
              )}
            </div>
          </div>
          <div className='detail-columns w-row'>
            <div className='w-col w-col-6'>
              <div className='_16-black-label'>Listing Type</div>
              <div className='_26-black'>
                {listingTypes.getLabel(listingType)}
              </div>
            </div>
            <div className='w-col w-col-6'>
              {listingType === 'ACQUISITIONS_AND_DIVESTITURES' ? (
                <Fragment>
                  <div className='_16-black-label'>Primary&nbsp;Objective</div>
                  <div className='_26-black'>{primaryObjective}</div>
                </Fragment>
              ) : typeOfOffer === 'Carbon_Credit' ? (
                <Fragment>
                  <div className='_16-black-label'>Credit Issued</div>
                  <div className='_26-black'>{creditIssued || 'N/A'}</div>
                </Fragment>
              ) : (
                <Fragment>
                  <div className='_16-black-label'>Sector</div>
                  <div className='_26-black'>
                    {sectors.getUniqueLabel(sector) || 'N/A'}
                  </div>
                </Fragment>
              )}
            </div>
          </div>
          <div className='detail-columns w-row'>
            <div className='w-col w-col-6'>
              {listingType === 'ACQUISITIONS_AND_DIVESTITURES' ? (
                <Fragment>
                  <div className='_16-black-label'>County</div>
                  <div className='_26-black'>{county}</div>
                </Fragment>
              ) : typeOfOffer === 'Carbon_Credit' ? (
                <Fragment>
                  <div className='_16-black-label'>Region</div>
                  <div className='_26-black'>{region || 'N/A'}</div>
                </Fragment>
              ) : (
                <Fragment>
                  <div className='_16-black-label'>Investment Instrument</div>
                  <div className='_26-black'>
                    {investmentInstruments.getLabel(investmentInstrument) ||
                      'N/A'}
                  </div>
                </Fragment>
              )}
            </div>
            {typeOfOffer === 'Carbon_Credit' ? (
              <div className='w-col w-col-6'>
                <div className='_16-black-label'>Vintage</div>
                <div className='_26-black'>{vintage || 'N/A'}</div>
              </div>
            ) : (
              <div className='w-col w-col-6'>
                <div className='_16-black-label'>Area</div>
                <div className='_26-black'>{areas.getLabel(area) || 'N/A'}</div>
              </div>
            )}
          </div>
        </div>
        <div className='column w-clearfix w-col w-col-4'>
          <div
            className='company-logo-container'
            style={{ backgroundImage: `url(${issuerCompany.logoUrl})` }}
          />
          {investmentInstrument == 'CONVERTIBLE_NOTE' ||
            (investmentInstrument == 'SAFE' && (
              <Fragment>
                <div className='detail-columns w-row'>
                  <div className='w-col w-col-6'>
                    <Fragment>
                      <div className='_16-black-label'>Value Cap</div>
                      <div className='_26-black'>
                        {formatAllCurrency(valuationCap, offerCurrency) ||
                          'N/A'}
                      </div>
                    </Fragment>
                  </div>
                  <div className='w-col w-col-6'>
                    <Fragment>
                      <div className='_16-black-label'>Discount Rate %</div>
                      <div className='_26-black'>{discountRate || 'N/A'}</div>
                    </Fragment>
                  </div>
                </div>

                <div className='detail-columns w-row'>
                  <div className='w-col w-col-6'>
                    <Fragment>
                      <div className='_16-black-label'>Coupon % </div>
                      <div className='_26-black'>{coupon || 'N/A'}</div>
                    </Fragment>
                  </div>
                  <div className='w-col w-col-6'>
                    <Fragment>
                      <div className='_16-black-label'>Maturity</div>
                      <div className='_26-black'>{maturity || 'N/A'}</div>
                    </Fragment>
                  </div>
                </div>
              </Fragment>
            ))}
        </div>
      </div>
    </div>
  );
};

OfferDetails.propTypes = {
  offer: PropTypes.object.isRequired,
};

export default OfferDetails;



// WEBPACK FOOTER //
// ./src/components/OfferOverview/OfferDetails.js